import React, { useState, useRef } from 'react'
import {
  Button,
  MenuItem,
  Typography,
  Box,
  IconButton,
} from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import PopoverMenu from 'src/components/PopoverMenu'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '@iconify/react'
import { CoreButton } from 'src/components/core'
import { messageBoxShowAction } from 'src/reducers/message-box/action'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import WorkspaceEdit from 'src/components/workspace-edit'
import useWorkspaces from 'src/hooks/useWorkspaces'

const WorkspaceSwitcher = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const me = useSelector(state => state.authentication?.me)
  const anchorRef = useRef(null)
  const { workspaces, currentWorkspace, loading, switchWorkspace } =
    useWorkspaces()
  const [isOpen, setOpen] = useState(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleEditWorkspaceName = (workspace) => {
    handleClose()
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: 'Edit Workspace',
          draggable: false,
          maxWidth: '500px',
          component: (
            <WorkspaceEdit
              currentWorkspaceName={workspace?.shopName}
              currentWorkspaceId={workspace?.shopId}
            />
          ),
        }),
      ),
    )
  }

  const handleSwitchWorkspace = (shopId) => {
    handleClose()
    if (shopId !== currentWorkspace?.shopId) {
      switchWorkspace(shopId)
    }
  }

  const navigateInWorkspace = (workspace, path) => {
    handleClose()
    if (workspace.shopId === currentWorkspace?.shopId) {
      history.push(path)
    } else {
      switchWorkspace(workspace.shopId, path)
    }
  }

  const handleNavigateToMembers = (workspace) =>
    navigateInWorkspace(workspace, '/workspace/members')

  const handleNavigateToWebhooks = (workspace) =>
    navigateInWorkspace(workspace, '/workspace/integrations?view=webhooks')

  if (loading) {
    return (
      <Button disabled sx={{ textTransform: 'none', fontWeight: 600, fontSize: '14px' }}>
        Workspace...
      </Button>
    )
  }

  const canManageWorkspace = me?.permissionPreset !== 'Viewer' && me?.permissionPreset !== 'Editor'

  return (
    <>
      <CoreButton
        ref={anchorRef}
        onClick={handleOpen}
        variant="outlined"
        color="dark"
        size="small"
        endIcon={<Icon icon={isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} color="#002E47" />}
        style={{ fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', lineHeight: 1, color: '#002E47', textTransform: 'none', height: 32, padding: '7.5px 10px 7.5px 12px', borderRadius: 6, border: '1px solid #CBD0D7' }}
      >
        {currentWorkspace?.shopName ? `${currentWorkspace.shopName}'s Workspace` : 'Workspace'}
      </CoreButton>

      <PopoverMenu
        width={300}
        open={isOpen}
        onClose={handleClose}
        anchorEl={anchorRef.current}
      >
        {workspaces.map((workspace) => (
          <MenuItem
            key={workspace.shopId}
            onClick={() => handleSwitchWorkspace(workspace.shopId)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '8px',
              paddingBottom: '8px',
              paddingRight: '8px',
            }}
          >
            <Box style={{ display: 'flex', alignItems: 'center', overflow: 'hidden', marginRight: '8px' }}>
              {workspace.shopId === currentWorkspace?.shopId && (
                <Icon 
                  icon="mdi:domain" 
                  color="#637381" 
                  style={{ marginRight: 8, fontSize: 18, flexShrink: 0 }} 
                />
              )}
              <Typography fontSize="14px" fontWeight={400} noWrap title={`${workspace.shopName}'s Workspace`}>
                {`${workspace.shopName}'s Workspace`}
              </Typography>
            </Box>
            {canManageWorkspace && workspace.shopId === currentWorkspace?.shopId && (
              <Box style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleEditWorkspaceName(workspace)
                  }}
                  style={{ padding: '4px', marginRight: '4px' }}
                >
                  <Icon icon="mdi:pencil-outline" color="#637381" fontSize={16} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleNavigateToMembers(workspace)
                  }}
                  style={{ padding: '4px', marginRight: '4px' }}
                >
                  <Icon icon="mdi:account-multiple-outline" color="#637381" fontSize={16} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleNavigateToWebhooks(workspace)
                  }}
                  style={{ padding: '4px' }}
                  title="Webhooks"
                >
                  <Icon icon="mdi:webhook" color="#637381" fontSize={16} />
                </IconButton>
              </Box>
            )}
          </MenuItem>
        ))}
      </PopoverMenu>

    </>
  )
}

export default WorkspaceSwitcher
